import { inkAsset, inkPart } from "./ink-assets";
import { MOTIONS, type Facing, type Joint, type Motion } from "./motion";
import type { ArtState } from "./living-art";
import type { Vec2 } from "./types";

export const RIG_PARTS = [
  "front",
  "side",
  "back",
  "upper",
  "lower",
  "thigh",
  "boot",
  "tool",
] as const;
export type RigProfile = {
  core: number;
  hip: number;
  spread: number;
  shoulder: number;
  leg: number;
  shin: number;
  arm: number;
  forearm: number;
  foot: number;
  stride: number;
  runStride: number;
  lift: number;
  support: number;
  gait: "human" | "heavy" | "quick" | "root" | "crawler" | "wing" | "quadruped";
  attack:
    | "staff"
    | "axe"
    | "offer"
    | "fist"
    | "blades"
    | "flower"
    | "crossbow"
    | "fire"
    | "bite"
    | "spores"
    | "spear"
    | "moth"
    | "roots"
    | "shell";
};
const human: RigProfile = {
  core: 76,
  hip: 51,
  spread: 8,
  shoulder: 19,
  leg: 32,
  shin: 31,
  arm: 19,
  forearm: 19,
  foot: 12,
  stride: 108,
  runStride: 132,
  lift: 8,
  support: 0.56,
  gait: "human",
  attack: "staff",
};
export const RIGS: Record<string, RigProfile> = {
  hero: human,
  pumpkin: {
    ...human,
    core: 91,
    hip: 28,
    spread: 24,
    shoulder: 36,
    leg: 21,
    shin: 20,
    arm: 23,
    forearm: 23,
    foot: 19,
    stride: 76,
    runStride: 90,
    lift: 5,
    support: 0.65,
    gait: "heavy",
    attack: "axe",
  },
  coconut: {
    ...human,
    core: 91,
    hip: 30,
    spread: 17,
    shoulder: 25,
    leg: 23,
    shin: 21,
    arm: 22,
    forearm: 22,
    foot: 16,
    stride: 80,
    runStride: 94,
    lift: 5,
    support: 0.63,
    gait: "root",
    attack: "offer",
  },
  wintermelon: {
    ...human,
    core: 89,
    hip: 26,
    spread: 25,
    shoulder: 35,
    leg: 20,
    shin: 19,
    arm: 24,
    forearm: 24,
    foot: 21,
    stride: 72,
    runStride: 84,
    lift: 4,
    support: 0.69,
    gait: "heavy",
    attack: "fist",
  },
  banana: {
    ...human,
    core: 84,
    hip: 38,
    spread: 9,
    shoulder: 19,
    leg: 28,
    shin: 26,
    arm: 21,
    forearm: 20,
    stride: 106,
    runStride: 124,
    lift: 9,
    gait: "quick",
    attack: "blades",
  },
  moonherb: {
    ...human,
    core: 87,
    hip: 31,
    spread: 8,
    shoulder: 17,
    leg: 23,
    shin: 22,
    arm: 20,
    forearm: 19,
    stride: 86,
    runStride: 99,
    lift: 4,
    support: 0.62,
    attack: "flower",
  },
  carrot: {
    ...human,
    core: 80,
    hip: 31,
    spread: 9,
    shoulder: 18,
    leg: 24,
    shin: 23,
    arm: 17,
    forearm: 18,
    stride: 92,
    runStride: 108,
    lift: 10,
    gait: "quick",
    attack: "crossbow",
  },
  chili: {
    ...human,
    core: 91,
    hip: 29,
    spread: 8,
    shoulder: 17,
    leg: 23,
    shin: 21,
    arm: 18,
    forearm: 18,
    stride: 83,
    runStride: 94,
    lift: 6,
    gait: "quick",
    attack: "fire",
  },
  mite: {
    ...human,
    core: 62,
    hip: 20,
    spread: 23,
    shoulder: 14,
    leg: 22,
    shin: 24,
    foot: 9,
    stride: 71,
    runStride: 88,
    lift: 6,
    support: 0.63,
    gait: "crawler",
    attack: "bite",
  },
  brute: {
    ...human,
    core: 91,
    hip: 26,
    spread: 26,
    shoulder: 36,
    leg: 23,
    shin: 21,
    arm: 28,
    forearm: 28,
    foot: 23,
    stride: 76,
    runStride: 89,
    lift: 4,
    support: 0.7,
    gait: "heavy",
    attack: "fist",
  },
  spitter: {
    ...human,
    core: 103,
    hip: 17,
    spread: 22,
    shoulder: 24,
    leg: 16,
    shin: 16,
    arm: 23,
    forearm: 23,
    foot: 10,
    stride: 54,
    runStride: 64,
    lift: 3,
    support: 0.68,
    gait: "root",
    attack: "spores",
  },
  sentinel: {
    ...human,
    core: 83,
    hip: 37,
    spread: 15,
    shoulder: 26,
    leg: 28,
    shin: 26,
    arm: 21,
    forearm: 21,
    foot: 17,
    stride: 90,
    runStride: 111,
    lift: 5,
    support: 0.64,
    gait: "heavy",
    attack: "spear",
  },
  moth: {
    ...human,
    core: 63,
    hip: 36,
    spread: 8,
    shoulder: 8,
    leg: 10,
    shin: 10,
    arm: 25,
    forearm: 25,
    foot: 8,
    stride: 94,
    runStride: 112,
    lift: 0,
    gait: "wing",
    attack: "moth",
  },
  "thorn-warden": {
    ...human,
    core: 101,
    hip: 27,
    spread: 27,
    shoulder: 34,
    leg: 25,
    shin: 23,
    arm: 30,
    forearm: 30,
    foot: 21,
    stride: 85,
    runStride: 107,
    lift: 5,
    support: 0.7,
    gait: "heavy",
    attack: "roots",
  },
  "ember-tortoise": {
    ...human,
    core: 77,
    hip: 23,
    spread: 30,
    shoulder: 20,
    leg: 23,
    shin: 23,
    arm: 25,
    forearm: 25,
    foot: 20,
    stride: 80,
    runStride: 96,
    lift: 5,
    support: 0.68,
    gait: "quadruped",
    attack: "shell",
  },
};
const tau = Math.PI * 2;
const ease = (x: number) => {
  x = Math.max(0, Math.min(1, x));
  return x * x * (3 - 2 * x);
};
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
export const rigStride = (
  id: string,
  m: Motion,
  face: Facing = "east",
  travel?: Vec2,
) => {
  const r = RIGS[id];
  return (
    (r ? (m === "run" ? r.runStride : r.stride) : 60) *
    (travel
      ? 1 - 0.35 * Math.abs(travel.y)
      : face === "north" || face === "south"
        ? 0.65
        : 1)
  );
};
/** A planted foot moves opposite the root exactly linearly; swing returns with zero endpoint velocity. */
export function footCycle(r: RigProfile, m: Motion, phase: number) {
  const t = ((phase % 1) + 1) % 1,
    stride = m === "run" ? r.runStride : r.stride,
    support = m === "run" ? Math.min(0.56, r.support) : r.support;
  const travel = stride * support;
  return t < support
    ? { forward: travel / 2 - stride * t, height: 0, planted: true }
    : {
        forward: mix(
          -travel / 2,
          travel / 2,
          ease((t - support) / (1 - support)),
        ),
        height:
          -Math.sin(((t - support) / (1 - support)) * Math.PI) *
          (m === "run" ? r.lift * 1.6 : r.lift),
        planted: false,
      };
}
type P3 = [number, number, number]; // lateral, forward, up/down; no whole-character image deformation.
function solve(root: Joint, end: Joint, a: number, b: number, bend = 1): Joint {
  const dx = end[0] - root[0],
    dy = end[1] - root[1],
    d = Math.max(0.001, Math.hypot(dx, dy));
  const u = (a * a - b * b + d * d) / (2 * d),
    h = Math.sqrt(Math.max(0, a * a - u * u));
  return [
    root[0] + (dx / d) * u + (dy / d) * h * bend,
    root[1] + (dy / d) * u - (dx / d) * h * bend,
  ];
}
const project = (p: P3, face: Facing, travel?: Vec2): Joint =>
  travel
    ? [
        (face === "south" ? p[0] : face === "north" ? -p[0] : p[0] * 0.25) +
          p[1] *
            (1 - 0.35 * Math.abs(travel.y)) *
            travel.x *
            (face === "west" ? -1 : 1),
        p[2] +
          (face === "east" || face === "west" ? p[0] * 0.15 : 0) +
          p[1] * (1 - 0.35 * Math.abs(travel.y)) * travel.y,
      ]
    : face === "south"
      ? [p[0], p[2] + p[1] * 0.65]
      : face === "north"
        ? [-p[0], p[2] - p[1] * 0.65]
        : [p[1] + p[0] * 0.25, p[2] + p[0] * 0.15];
function knee3D(root: P3, foot: P3, a: number, b: number): P3 {
  const d = foot.map((v, i) => v - root[i]) as P3,
    len = Math.hypot(...d),
    u = d.map((v) => v / len) as P3;
  const along = (a * a - b * b + len * len) / (2 * len),
    height = Math.sqrt(Math.max(0, a * a - along * along));
  const bend = [-u[0] * u[1], 1 - u[1] * u[1], -u[2] * u[1]],
    norm = Math.hypot(...bend);
  return root.map((v, i) => v + u[i] * along + (bend[i] / norm) * height) as P3;
}
export function botanicalPose(
  id: string,
  m: Motion,
  phase: number,
  face: Facing = "east",
  travel?: Vec2,
  gait?: ArtState["gait"],
) {
  const r = RIGS[id],
    p = Math.max(0, Math.min(1, phase)),
    angle = p * tau;
  const moving = !!gait || ["walk", "run", "start", "stop", "rush"].includes(m),
    gaitPhase = gait?.phase ?? p,
    gaitMotion = gait?.motion ?? m,
    gaitAngle = gaitPhase * tau,
    stride = rigStride(id, gaitMotion);
  const hit = MOTIONS[m][2] >= 0 ? MOTIONS[m][2] / (MOTIONS[m][0] - 1) : 0.5;
  // Continuous anticipation -> contact -> recovery. Contact pose occurs at the World event frame.
  const charge = p < hit ? ease(p / hit) : 1 - ease((p - hit) / (1 - hit));
  const strike =
    p < hit
      ? 0
      : Math.sin(Math.min(1, (p - hit) / Math.max(0.12, 1 - hit)) * Math.PI);
  const windup =
    ease(p / (hit * 0.62)) * (1 - ease((p - hit * 0.62) / (hit * 0.38)));
  const contact =
    ease((p - hit * 0.62) / (hit * 0.38)) *
    (1 - ease((p - hit) / Math.max(0.12, 1 - hit)));
  const collapse = m === "death" ? ease(p) : m === "rest" ? 0.62 : 0;
  let crouch = collapse * r.hip * 0.64;
  if (["gather", "plant", "harvest", "craft"].includes(m))
    crouch += Math.sin(p * Math.PI) * (m === "craft" ? 8 : 16);
  if (m === "slam") crouch += -7 * windup + 15 * contact;
  if (m === "dash" || m === "rush") crouch += Math.sin(p * Math.PI) * 8;
  if (m === "hurt" || m === "stagger") crouch += Math.sin(p * Math.PI) * 5;
  const bodyBob = moving
    ? Math.sin(gaitAngle * 2) * (r.gait === "heavy" ? 1.4 : 1)
    : 0;
  const standingHip = (r.leg + r.shin) * 0.91;
  const movingHip = Math.min(standingHip, r.hip + (r.gait === "heavy" ? 3 : 0));
  const gaitBlend =
    m === "start" ? ease(p) : m === "stop" ? 1 - ease(p) : moving ? 1 : 0;
  let hipY = -mix(standingHip, movingHip, gaitBlend) + crouch + bodyBob;
  const weight = moving && r.gait === "heavy" ? Math.sin(gaitAngle) * 2.5 : 0;
  const cycles = [0, 1].map((i) =>
    moving
      ? footCycle(r, gaitMotion, gaitPhase + i * 0.5)
      : m === "dash"
        ? {
            forward: (i ? -19 : 22) * Math.sin(p * Math.PI),
            height: -Math.sin(p * Math.PI) * 11,
            planted: p < 0.12 || p > 0.9,
          }
        : m === "turn"
          ? {
              forward: (i ? -1 : 1) * (3 + 7 * Math.sin(p * Math.PI)),
              height: i ? -4 * Math.sin(p * Math.PI) : 0,
              planted: i === 0,
            }
          : { forward: i ? -3 : 3, height: 0, planted: true },
  );
  for (const c of cycles) {
    const reach = r.leg + r.shin - 0.3;
    hipY = Math.max(
      hipY,
      c.height -
        Math.sqrt(
          Math.max(1, reach * reach - c.forward * c.forward - weight * weight),
        ),
    );
  }
  const legs = ([-1, 1] as const).map((side, i) => {
    const c = cycles[i];
    const ramp = m === "start" ? ease(p) : m === "stop" ? 1 - ease(p) : 1;
    const f: P3 = [side * r.spread, c.forward * ramp, c.height],
      root: P3 = [side * r.spread + weight, 0, hipY];
    // Sagittal IK is solved BEFORE projection; front/back knees bend forward, never outward.
    const knee = knee3D(root, f, r.leg, r.shin);
    return {
      hip: project(root, face, travel),
      knee: project(knee, face, travel),
      foot: project(f, face, travel),
      hip3: root,
      knee3: knee,
      foot3: f,
      planted: c.planted,
      side,
    };
  });
  const sideView = face === "east" || face === "west",
    shoulderY = hipY - r.core * 0.48;
  const shoulders: Joint[] = [
    [-r.shoulder * (sideView ? 0.22 : 1), shoulderY - 2],
    [r.shoulder * (sideView ? 0.22 : 1), shoulderY + 1],
  ];
  const hands: Joint[] = shoulders.map(([x, y], i) => [
    x + (sideView ? (i ? 17 : -4) : i ? 7 : -7),
    y + r.arm + r.forearm - 7,
  ]);
  if (m === "idle" || m === "guard" || m === "rest")
    for (let i = 0; i < 2; i++)
      hands[i][1] += Math.sin(angle + (i ? 0.2 : 0)) * 0.8;
  const swing = moving ? Math.sin(gaitAngle) * Math.min(8, stride * 0.065) : 0;
  hands[0][0] += sideView ? swing : 0;
  hands[0][1] += sideView ? 0 : swing * 0.55;
  // Most tools stay supported, without the uncontrolled mirrored human arm swing.
  hands[1] = [
    shoulders[1][0] + (sideView ? 20 : 8),
    shoulderY + 22 + (moving ? Math.sin(gaitAngle) * 1.2 : 0),
  ];
  if (
    ["attack", "cast", "heal", "volley", "slam", "chop", "mine"].includes(m)
  ) {
    const a = r.attack;
    if (a === "axe" || m === "chop" || m === "mine")
      hands[1] = [
        shoulders[1][0] - 8 * windup + 25 * contact,
        shoulderY + 16 - 37 * windup + contact * 16,
      ];
    else if (a === "fist" || a === "roots" || m === "slam")
      for (let i = 0; i < 2; i++)
        hands[i] = [
          shoulders[i][0] + (i ? 12 : -12) * contact,
          shoulderY + 18 - 43 * windup + contact * 18,
        ];
    else if (a === "spear") {
      hands[0] = [shoulders[0][0] + 8, shoulderY + 14];
      hands[1] = [
        shoulders[1][0] + 10 - 9 * windup + 25 * contact,
        shoulderY + 16,
      ];
    } else if (a === "crossbow") {
      hands[1] = [shoulders[1][0] + 23, shoulderY + 14];
      hands[0] = [shoulders[0][0] + 8 - 12 * charge, shoulderY + 16];
    } else if (a === "blades") {
      hands[1] = [shoulders[1][0] + 25 * charge, shoulderY - 9 * charge + 12];
      hands[0] = [shoulders[0][0] - 23 * charge, shoulderY + 11 + 15 * strike];
    } else if (a === "offer" || a === "flower") {
      for (let i = 0; i < 2; i++)
        hands[i] = [
          shoulders[i][0] + (i ? 1 : -1) * 20 * charge,
          shoulderY + 17 - 14 * charge,
        ];
    } else
      hands[1] = [
        shoulders[1][0] + 12 + 17 * charge,
        shoulderY + 15 - 28 * charge,
      ];
  }
  if (["gather", "plant", "harvest", "interact"].includes(m))
    hands[1] = [
      shoulders[1][0] + 20 * Math.sin(p * Math.PI),
      shoulderY + 30 + 8 * Math.sin(p * Math.PI),
    ];
  if (m === "craft")
    hands[1] = [
      shoulders[1][0] + 19,
      shoulderY + 10 - 17 * Math.abs(Math.sin(p * Math.PI * 3)),
    ];
  if (m === "water")
    hands[1] = [shoulders[1][0] + 21, shoulderY + 20 - 5 * charge];
  if (m === "eat")
    hands[1] = [shoulders[1][0] - 3 * charge, shoulderY + 20 - 30 * charge];
  if (m === "guard") hands[1] = [shoulders[1][0] + 12, shoulderY + 7];
  if (m === "social" || m === "cheer")
    hands[1] = [
      shoulders[1][0] + 18 * charge,
      shoulderY - 25 * charge + Math.sin(angle * 3) * 3,
    ];
  if (m === "observe")
    hands[0] = [
      shoulders[0][0] + 6,
      shoulderY + 23 - 23 * Math.sin(p * Math.PI),
    ];
  if (collapse)
    for (let i = 0; i < 2; i++)
      hands[i] = [shoulders[i][0] + (i ? 5 : -5), shoulderY + 30];
  if (m === "hurt" || m === "stagger")
    for (let i = 0; i < 2; i++)
      hands[i] = [
        shoulders[i][0] + (i ? -8 : 8) * Math.sin(p * Math.PI),
        shoulderY + 20 - 12 * Math.sin(p * Math.PI),
      ];
  if (m === "turn")
    for (let i = 0; i < 2; i++)
      hands[i][0] += (i ? 1 : -1) * Math.sin(p * Math.PI) * 12;
  const arms = shoulders.map((root, i) => {
    const end = hands[i],
      dx = end[0] - root[0],
      dy = end[1] - root[1],
      d = Math.hypot(dx, dy),
      max = r.arm + r.forearm - 0.3,
      min = Math.abs(r.arm - r.forearm) + 0.3;
    if (d > max) {
      end[0] = root[0] + (dx / d) * max;
      end[1] = root[1] + (dy / d) * max;
    }
    if (d < min) {
      end[0] = root[0] + (d > 0.001 ? dx / d : 0) * min;
      end[1] = root[1] + (d > 0.001 ? dy / d : 1) * min;
    }
    return {
      root,
      elbow: solve(root, end, r.arm, r.forearm, sideView ? 1 : i ? 1 : -1),
      hand: end,
    };
  });
  const core: Joint = [weight, hipY + 10],
    coreAngle = m === "death" ? (-Math.PI / 2) * ease((p - 0.2) / 0.8) : 0;
  if (coreAngle)
    for (const arm of arms)
      for (const point of [arm.root, arm.elbow, arm.hand]) {
        const x = point[0] - core[0],
          y = point[1] - core[1];
        point[0] = core[0] + x * Math.cos(coreAngle) - y * Math.sin(coreAngle);
        point[1] = core[1] + x * Math.sin(coreAngle) + y * Math.cos(coreAngle);
      }
  return {
    legs,
    arms,
    hipY,
    weight,
    collapse,
    charge,
    strike,
    windup,
    contact,
    hit,
    core,
    coreAngle,
    gaitPhase,
    gaitMotion,
  };
}

function segment(
  ctx: CanvasRenderingContext2D,
  key: string,
  a: Joint,
  b: Joint,
  length: number,
  shade = false,
) {
  const im = inkAsset(key)!;
  ctx.save();
  if (shade) ctx.globalAlpha *= 0.88;
  ctx.translate(...a);
  ctx.rotate(Math.atan2(b[1] - a[1], b[0] - a[0]) - Math.PI / 2);
  // Bone-space length sets thickness; projected endpoint sets foreshortening only.
  const w = (im.width / im.height) * (length + 6),
    h = Math.hypot(b[0] - a[0], b[1] - a[1]) + 6;
  ctx.drawImage(im, -w / 2, -3, w, h);
  ctx.restore();
}
function debugChain(
  ctx: CanvasRenderingContext2D,
  points: Joint[],
  near: boolean,
) {
  ctx.strokeStyle = near ? "#e444a8" : "#16cde4";
  ctx.lineWidth = 1;
  ctx.beginPath();
  points.forEach((p, i) => (i ? ctx.lineTo(...p) : ctx.moveTo(...p)));
  ctx.stroke();
  for (const p of points) {
    ctx.beginPath();
    ctx.arc(...p, 1.8, 0, tau);
    ctx.fillStyle = ctx.strokeStyle;
    ctx.fill();
  }
}
function drawBoot(
  ctx: CanvasRenderingContext2D,
  key: string,
  knee: Joint,
  foot: Joint,
  size: number,
  sole: number,
  shade: boolean,
) {
  const im = inkAsset(key)!;
  ctx.save();
  if (shade) ctx.globalAlpha *= 0.88;
  ctx.save();
  ctx.translate(...knee);
  ctx.rotate(Math.atan2(foot[1] - knee[1], foot[0] - knee[0]) - Math.PI / 2);
  const h = Math.hypot(foot[0] - knee[0], foot[1] - knee[1]) + 4,
    w = (im.width / (im.height * 0.7)) * (size + 3);
  ctx.drawImage(im, 0, 0, im.width, im.height * 0.7, -w / 2, -2, w, h);
  ctx.restore();
  const sh = im.height * 0.3,
    sw = sole * 1.5,
    soleHeight = (sw * sh) / im.width;
  ctx.drawImage(
    im,
    0,
    im.height * 0.7,
    im.width,
    sh,
    foot[0] - sw * 0.48,
    foot[1] - soleHeight * 0.82,
    sw,
    soleHeight,
  );
  ctx.restore();
}
export function drawBotanicalActor(
  ctx: CanvasRenderingContext2D,
  id: string,
  s: ArtState,
): boolean {
  const r = RIGS[id];
  if (!r || !RIG_PARTS.every((k) => inkAsset(`v8/${id}/${k}`))) return false;
  const prefix = `v8/${id}/`,
    face = s.facing ?? "east";
  let view = face === "north" ? "back" : face === "south" ? "front" : "side";
  if (s.motion === "turn" && s.phase < 0.45)
    view = view === "side" ? "front" : "side";
  const m = s.motion,
    p = s.phase,
    time = s.time ?? p * 2,
    moving = !!s.gait || ["walk", "run", "start", "stop", "rush"].includes(m);
  ctx.save();
  if (face === "west") ctx.scale(-1, 1);
  const pose = botanicalPose(id, m, p, face, s.travel, s.gait);
  if (r.gait === "crawler" || r.gait === "quadruped") {
    const count = r.gait === "crawler" ? 6 : 4;
    const legs = Array.from({ length: count }, (_, i) => {
      const side = i < count / 2 ? -1 : 1,
        j = i % (count / 2),
        phase = (pose.gaitPhase + (j % 2) * 0.5 + (side < 0 ? 0.5 : 0)) % 1;
      const f = moving
        ? footCycle(r, pose.gaitMotion, phase)
        : { forward: 0, height: 0, planted: true };
      const longitudinal = (j - (count / 2 - 1) / 2) * (count === 6 ? 18 : 36);
      const root3: P3 = [side * 12, longitudinal, -23 + pose.collapse * 12],
        foot3: P3 = [side * 34, longitudinal + f.forward, f.height];
      if (m === "slam" && j === 0) foot3[2] -= pose.windup * 18;
      if (m === "death") foot3[0] = mix(foot3[0], side * 17, pose.collapse);
      const knee = knee3D(root3, foot3, r.leg, r.shin);
      return {
        root: project(root3, face, s.travel),
        knee: project(knee, face, s.travel),
        foot: project(foot3, face, s.travel),
        near: side > 0,
      };
    });
    const leg = (l: (typeof legs)[number]) => {
      segment(ctx, prefix + "thigh", l.root, l.knee, r.leg, !l.near);
      segment(ctx, prefix + "boot", l.knee, l.foot, r.shin, !l.near);
    };
    legs.filter((l) => !l.near).forEach(leg);
    inkPart(ctx, prefix + view, 0, -6 + pose.collapse * 17, r.core, 0.5, 1);
    legs.filter((l) => l.near).forEach(leg);
    if (m === "attack" || m === "volley")
      inkPart(
        ctx,
        prefix + "tool",
        ...project([0, 31 + pose.contact * 7, -17], face),
        r.gait === "crawler" ? 22 : 32,
        0.5,
        0.65,
        -0.15 * pose.charge,
      );
    if (s.audit)
      for (const l of legs) debugChain(ctx, [l.root, l.knee, l.foot], l.near);
  } else if (r.gait === "wing") {
    const flap = Math.sin(p * tau * (moving ? 2 : 3)),
      lift = pose.collapse * 25;
    for (const [part, sign] of [
      ["upper", -1],
      ["lower", 1],
    ] as const) {
      inkPart(
        ctx,
        prefix + part,
        sign * 5,
        -55 + lift,
        91,
        sign < 0 ? 0.07 : 0.93,
        0.58,
        sign * flap * 0.27 * (1 - pose.collapse),
      );
    }
    inkPart(ctx, prefix + view, 0, -17 + lift, r.core, 0.5, 1);
    inkPart(
      ctx,
      prefix + "tool",
      18,
      -42 + lift,
      49,
      0.5,
      0.65,
      -0.12 - pose.charge * 0.3,
    );
    if (s.audit)
      debugChain(
        ctx,
        [
          [-55, -55],
          [0, -55],
          [55, -55],
        ],
        true,
      );
  } else {
    const drawLeg = (i: number) => {
      const l = pose.legs[i];
      const thigh = inkAsset(`v8/clothing/${id}-thigh`)
        ? `v8/clothing/${id}-thigh`
        : prefix + "thigh";
      const boot = inkAsset(`v8/clothing/${id}-boot`)
        ? `v8/clothing/${id}-boot`
        : prefix + "boot";
      segment(ctx, thigh, l.hip, l.knee, r.leg, i === 0);
      drawBoot(ctx, boot, l.knee, l.foot, r.shin, r.foot, i === 0);
    };
    const drawArm = (i: number) => {
      const a = pose.arms[i];
      segment(ctx, prefix + "upper", a.root, a.elbow, r.arm, i === 0);
      segment(ctx, prefix + "lower", a.elbow, a.hand, r.forearm, i === 0);
    };
    drawLeg(0);
    drawArm(0);
    drawLeg(1);
    const garmentView = id === "pumpkin" && view === "back" ? "front" : view;
    const garment = `v8/clothing/${id}-${garmentView}`;
    if (inkAsset(garment)) {
      const height = id === "hero" ? 47 : id === "moonherb" ? 49 : 31;
      inkPart(
        ctx,
        garment,
        pose.core[0] + Math.sin(pose.coreAngle) * 20,
        pose.core[1] - Math.cos(pose.coreAngle) * 20,
        height,
        0.5,
        0,
        pose.coreAngle +
          (moving
            ? Math.sin(p * tau + 0.4) * 0.045
            : Math.sin(time * 1.1) * 0.012),
      );
    }
    inkPart(ctx, prefix + view, ...pose.core, r.core, 0.5, 1, pose.coreAngle);
    const h = pose.arms[1].hand;
    const noTool =
      [
        "plant",
        "gather",
        "harvest",
        "eat",
        "social",
        "cheer",
        "rest",
        "death",
      ].includes(m) || ["fist", "roots", "spores"].includes(r.attack);
    if (!noTool) {
      const key =
        m === "chop"
          ? "hero/axe"
          : m === "mine"
            ? "hero/pick"
            : m === "water"
              ? "hero/can"
              : r.attack === "crossbow" && inkAsset("v8/clothing/carrot-bow")
                ? "v8/clothing/carrot-bow"
                : prefix + "tool";
      const angle =
        m === "chop" || m === "mine" || (r.attack === "axe" && m === "attack")
          ? -1.1 * pose.windup + pose.contact * 0.9
          : r.attack === "blades" && m === "attack"
            ? -1.3 + pose.charge * 2
            : r.attack === "crossbow"
              ? 0
              : r.attack === "spear" && m === "attack"
                ? (Math.PI / 2) * pose.charge
                : 0.08;
      if (r.attack === "spear") {
        inkPart(ctx, key, ...pose.arms[0].hand, 50, 0.5, 0.52, -0.08);
        if (inkAsset("v8/clothing/sentinel-spear"))
          inkPart(
            ctx,
            "v8/clothing/sentinel-spear",
            ...h,
            84,
            0.5,
            0.69,
            m === "attack" ? Math.PI / 2 : 0.15,
          );
        else {
          // Ink-lined ash shaft, bronze socket and amber blade; hand remains its grip pivot.
          ctx.save();
          ctx.translate(...h);
          ctx.rotate(m === "attack" ? Math.PI / 2 : 0.15);
          ctx.strokeStyle = "#423e2a";
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.moveTo(0, 23);
          ctx.lineTo(0, -39);
          ctx.stroke();
          ctx.strokeStyle = "#baa473";
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.fillStyle = "#d3a552";
          ctx.strokeStyle = "#584c32";
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          ctx.moveTo(0, -60);
          ctx.lineTo(7, -41);
          ctx.lineTo(0, -32);
          ctx.lineTo(-5, -41);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(0, -55);
          ctx.lineTo(0, -35);
          ctx.stroke();
          ctx.restore();
        }
      } else
        inkPart(
          ctx,
          key,
          ...h,
          m === "water" ? 25 : r.attack === "crossbow" ? 24 : 68,
          0.48,
          r.attack === "crossbow" ? 0.5 : 0.69,
          angle,
        );
    }
    drawArm(1);
    if (r.attack === "blades" && !noTool)
      inkPart(
        ctx,
        prefix + "tool",
        ...pose.arms[0].hand,
        57,
        0.5,
        0.8,
        -0.35 - pose.strike * 1.2,
      );
    if (r.attack === "spores" && ["attack", "volley"].includes(m)) {
      for (let i = 0; i < 3; i++)
        inkPart(
          ctx,
          prefix + "tool",
          (i - 1) * 20,
          -36 - pose.charge * 18,
          21,
          0.5,
          1,
          (i - 1) * 0.2,
        );
    }
    if (["cast", "heal", "water", "plant", "eat"].includes(m)) {
      ctx.fillStyle =
        m === "water" ? "#82b9c8" : m === "eat" ? "#ce904b" : "#d0dd9d";
      for (let i = 0; i < (m === "eat" ? 1 : 5); i++) {
        const a = i * 2.4;
        ctx.beginPath();
        ctx.ellipse(
          h[0] + Math.cos(a) * pose.charge * 12,
          h[1] + (m === "water" ? p * 25 : -Math.sin(a) * pose.charge * 12),
          2,
          3,
          a,
          0,
          tau,
        );
        ctx.fill();
      }
    }
    if (s.audit) {
      for (let i = 0; i < 2; i++) {
        const l = pose.legs[i],
          a = pose.arms[i];
        debugChain(ctx, [l.hip, l.knee, l.foot], i === 1);
        debugChain(ctx, [a.root, a.elbow, a.hand], i === 1);
      }
    }
  }
  ctx.restore();
  return true;
}
